import React from "react";
import { FiCode, FiFileText, FiGrid, FiList, FiShare2, FiUploadCloud } from "react-icons/fi";
import { LoadingSpinner } from "../LoadingSpinner";
import { formatExactDate, formatRelativeTime } from "../../Helper/dates";

type DeploymentState = "up-to-date" | "needs-deploy" | "pending";

const deploymentStateStyles: Record<
	DeploymentState,
	{ badge: string; indicator: string; statusLabel: string }
> = {
	"up-to-date": {
		badge: "border-emerald-100 bg-emerald-50 text-emerald-700",
		indicator: "bg-emerald-500",
		statusLabel: "Up to date"
	},
	"needs-deploy": {
		badge: "border-amber-100 bg-amber-50 text-amber-700",
		indicator: "bg-amber-500",
		statusLabel: "Out of sync"
	},
	pending: {
		badge: "border-slate-100 bg-slate-50 text-slate-600",
		indicator: "bg-slate-400",
		statusLabel: "Pending deploy"
	}
};

const renderDeploymentChip = (label: string, state: DeploymentState, deployedAt?: string | null) => {
	const styles = deploymentStateStyles[state];
	const showInfo = state !== "up-to-date";
	const relative = deployedAt ? formatRelativeTime(deployedAt) : "Not deployed";
	const exact = deployedAt ? formatExactDate(deployedAt) : undefined;
	return (
		<div
			className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${styles.badge}`}
			title={exact}
		>
			<span className={`inline-flex h-2 w-2 rounded-full ${styles.indicator}`} />
			<span>{label}</span>
			{showInfo && <span className="text-[11px] font-normal text-slate-600">{relative}</span>}
			{showInfo && <span className="text-[11px] font-semibold text-slate-700">{styles.statusLabel}</span>}
		</div>
	);
};

type PageEditorStickyActionsProps = {
	contentViewMode: "blocks" | "json" | "markdown";
	isJsonLocked: boolean;
	pageJsonError: string | null;
	onSelectBlocks: () => void;
	onSelectJson: () => void;
	onSelectMarkdown: () => void;
	onShowConnections: () => void;
	connectionsDisabled: boolean;
	onDeployPreview: () => void;
	deployPreviewDisabled: boolean;
	deployingPreview: boolean;
	previewState: DeploymentState;
	previewDeployedAt?: string | null;
	productionState: DeploymentState;
	productionDeployedAt?: string | null;
	/** Navigate to deployment page. Production deploy is done there, not here. */
	onGoToDeployment: () => void;
	/** Page tree (left nav) visible. Toggle via the bar before Editing view. */
	pageNavOpen: boolean;
	onTogglePageNav: () => void;
};

/** Page editor sticky action bar with editing mode, connections and deployment. */
const PageEditorStickyActions: React.FC<PageEditorStickyActionsProps> = ({
	contentViewMode,
	isJsonLocked,
	pageJsonError,
	onSelectBlocks,
	onSelectJson,
	onSelectMarkdown,
	onShowConnections,
	connectionsDisabled,
	onDeployPreview,
	deployPreviewDisabled,
	deployingPreview,
	previewState,
	previewDeployedAt,
	productionState,
	productionDeployedAt,
	onGoToDeployment,
	pageNavOpen,
	onTogglePageNav
}) => (
	<div className="w-full">
		<div className="rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-white/80">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex flex-wrap items-center gap-2">
					<button
						type="button"
						onClick={onTogglePageNav}
						className={`flex items-center justify-center rounded-l-full border py-1.5 px-3 transition ${
							pageNavOpen
								? "border-slate-300 bg-slate-100 text-slate-700"
								: "border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-600"
						}`}
						title={pageNavOpen ? "Hide page navigation" : "Show page navigation"}
						aria-label={pageNavOpen ? "Hide page navigation" : "Show page navigation"}
					>
						<FiList className="h-4 w-4" />
					</button>
					<div className="flex flex-wrap items-center gap-2 rounded-r-full border border-slate-200 border-l-0 bg-white px-3 py-1.5">
						<div className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
							<FiGrid className="h-3.5 w-3.5" /> Editing view
						</div>
						<div className="flex items-center gap-1">
							<button
								type="button"
								disabled={isJsonLocked}
								onClick={onSelectBlocks}
								className={`rounded-full px-2.5 py-1 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
									contentViewMode === "blocks"
										? "border border-slate-800 bg-slate-800 text-white shadow-sm"
										: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
								}`}
							>
								<FiGrid className="mr-1 inline-block" /> Blocks
							</button>
							<button
								type="button"
								onClick={onSelectMarkdown}
								className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${
									contentViewMode === "markdown"
										? "border border-slate-800 bg-slate-800 text-white shadow-sm"
										: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
								}`}
							>
								<FiFileText className="mr-1 inline-block" /> Markdown
							</button>
							<button
								type="button"
								onClick={onSelectJson}
								className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${
									contentViewMode === "json"
										? "border border-slate-800 bg-slate-800 text-white shadow-sm"
										: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
								}`}
							>
								<FiCode className="mr-1 inline-block" /> JSON
							</button>
						</div>
						{contentViewMode === "json" && pageJsonError && (
							<span className="text-[11px] font-semibold text-rose-600">JSON not valid</span>
						)}
					</div>
					<button
						type="button"
						onClick={onShowConnections}
						disabled={connectionsDisabled}
						className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						<FiShare2 className="mr-2" /> Show connections
					</button>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{renderDeploymentChip("Preview", previewState, previewDeployedAt)}
					<button
						type="button"
						onClick={onDeployPreview}
						disabled={deployPreviewDisabled}
						className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{deployingPreview ? (
							<>
								<LoadingSpinner size="sm" inline />
								<span className="ml-2">Deploying…</span>
							</>
						) : (
							<>
								<FiUploadCloud className="mr-2" /> Deploy preview
							</>
						)}
					</button>
					{renderDeploymentChip("Production", productionState, productionDeployedAt)}
					<button
						type="button"
						onClick={onGoToDeployment}
						className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
					>
						Production
					</button>
				</div>
			</div>
		</div>
	</div>
);

export default PageEditorStickyActions;
